import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Chip } from "@/components/sync/EndpointCard";
import { utc } from "@/components/sync/syncText";

const td = "px-2 py-1 border-t border-[var(--border-subtle)] align-top";
const SEV = { error: "text-rose-300", warning: "text-amber-300", info: "text-sky-300" };
const VER = { UNVERIFIED: "text-amber-300", "VERIFIED-EMPTY": "text-emerald-300", "NOT COLLECTED": "text-slate-500" };
const sel = "h-7 text-xs bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded";

export function SemanticsTable({ s }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid="tv-semantics" className="w-full text-[11px] text-slate-400">
        <thead><tr className="text-left text-slate-500">{["Data", "Status", "Latest result", "Sharekhan route", "Notes"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {Object.entries(s.semantics).map(([k, v]) => {
            const l = s.latest[k];
            return (
              <tr key={k} data-testid={`tv-sem-${k}`}>
                <td className={`${td} text-slate-200`}>{v.label}</td>
                <td className={`${td} font-semibold whitespace-nowrap ${VER[v.verification]}`} data-testid={`tv-sem-${k}-verification`}>{v.verification}</td>
                <td className={td} data-testid={`tv-sem-${k}-latest`}>{l ? <><Chip status={l.payload_class} /> <span className="text-slate-500">{l.row_count} rows · {utc(l.created_at)}</span></> : "—"}</td>
                <td className={`${td} font-mono break-all min-w-[12rem]`}>{v.route}</td>
                <td className={`${td} text-slate-500`}>{v.note}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ReportsTable() {
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const { data: d } = useQuery({ queryKey: ["tv-reports", kind, page], gcTime: 0, queryFn: async () => (await api.get("/validation/reports", { params: { kind: kind || undefined, page, page_size: 20 } })).data });
  const rows = d?.rows || [];
  const pages = d ? Math.max(1, Math.ceil(d.total / d.page_size)) : 1;
  return (
    <div className="space-y-2">
      <select data-testid="tv-reports-kind" value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} className={sel}>
        <option value="">All data types</option><option value="holdings">Holdings</option><option value="positions">Positions</option>
      </select>
      <div className="overflow-x-auto">
        <table data-testid="tv-reports" className="w-full text-[11px] text-slate-400">
          <thead><tr className="text-left text-slate-500">{["Run", "When", "Data", "Result", "Rows", "Accepted", "Rejected", "Errors", "Source"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.run_id}-${r.kind}`} data-testid={`tv-report-${r.run_id}-${r.kind}`}>
                <td className={`${td} font-mono text-slate-200`}>{r.run_id}</td><td className={td}>{utc(r.created_at)}</td><td className={td}>{r.kind}</td>
                <td className={td}><Chip status={r.payload_class} /></td><td className={td}>{r.row_count}</td>
                <td className={td}>{r.accepted_count ?? "—"}</td><td className={td}>{r.rejected_count ?? "—"}</td>
                <td className={`${td} text-rose-300`}>{r.severity_counts?.error || 0}</td>
                <td className={`${td} text-slate-500`}>{r.source === "sync" ? "validated at sync" : "from stored run details"}</td>
              </tr>
            ))}
            {d && !rows.length && <tr><td colSpan={9} data-testid="tv-reports-empty" className={`${td} text-slate-500`}>No validation results yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-[11px] text-slate-400">
        <span data-testid="tv-reports-total">{d?.total ?? 0} result(s){d?.truncated ? " · newest results only" : ""}</span>
        <div className="flex gap-2"><button data-testid="tv-reports-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-40">Prev</button><span>{page} / {pages}</span><button data-testid="tv-reports-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="disabled:opacity-40">Next</button></div>
      </div>
    </div>
  );
}

export function IssuesTable({ grant, severities }) {
  const [f, setF] = useState({ q: "", kind: "", severity: "", code: "", sort: "severity", desc: false, page: 1 });
  const [qIn, setQIn] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, page: k === "page" ? v : 1 }));
  useEffect(() => {
    const t = setTimeout(() => set("q", qIn), 350);
    return () => clearTimeout(t);
  }, [qIn]);
  const { data: d, isError } = useQuery({
    queryKey: ["tv-issues", grant, f], gcTime: 0,
    queryFn: async () => (await api.get("/validation/issues", { headers: { "X-Portfolio-Grant": grant }, params: { ...f, kind: f.kind || undefined, severity: f.severity || undefined, code: f.code || undefined, page_size: 25 } })).data,
  });
  const pages = d ? Math.max(1, Math.ceil(d.total / d.page_size)) : 1;
  if (isError) return <div data-testid="tv-issues-error" className="text-[11px] text-rose-300">Details are locked or expired. Unlock again.</div>;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Input data-testid="tv-issues-search" value={qIn} onChange={(e) => setQIn(e.target.value)} placeholder="Search symbol, code, field or run" className="h-7 w-56 text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
        <select data-testid="tv-issues-kind" value={f.kind} onChange={(e) => set("kind", e.target.value)} className={sel}><option value="">All data types</option>{["holdings", "positions", "orders", "fills"].map((k) => <option key={k} value={k}>{k}</option>)}</select>
        <select data-testid="tv-issues-severity" value={f.severity} onChange={(e) => set("severity", e.target.value)} className={sel}><option value="">All severities</option>{["error", "warning", "info"].map((k) => <option key={k} value={k}>{k}</option>)}</select>
        <select data-testid="tv-issues-code" value={f.code} onChange={(e) => set("code", e.target.value)} className={sel}><option value="">All issue types</option>{Object.keys(severities).map((k) => <option key={k} value={k}>{k}</option>)}</select>
        <select data-testid="tv-issues-sort" value={f.sort} onChange={(e) => set("sort", e.target.value)} className={sel}>{["severity", "code", "kind", "symbol", "run_id", "row"].map((k) => <option key={k} value={k}>{`Sort: ${k}`}</option>)}</select>
        <button data-testid="tv-issues-order" onClick={() => set("desc", !f.desc)} className="text-xs text-slate-300 hover:text-white">{f.desc ? "Descending" : "Ascending"}</button>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="tv-issues" className="w-full text-[11px] text-slate-400">
          <thead><tr className="text-left text-slate-500">{["Severity", "Issue", "Field", "Symbol", "Data", "Row", "Run"].map((h) => <th key={h} className="px-2 py-1 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {d?.rows.map((x, i) => (
              <tr key={`${x.run_id}-${x.kind}-${x.row}-${x.code}-${x.field}-${i}`} data-testid={`tv-issue-${i}`}>
                <td className={`${td} font-semibold ${SEV[x.severity]}`}>{x.severity}</td><td className={`${td} text-slate-200`}>{x.code}</td>
                <td className={`${td} font-mono`}>{x.field || "—"}</td><td className={td}>{x.symbol || "—"}</td><td className={td}>{x.kind}</td>
                <td className={td}>{x.row ?? "—"}</td><td className={`${td} font-mono`}>{x.run_id}</td>
              </tr>
            ))}
            {d && !d.rows.length && <tr><td colSpan={7} data-testid="tv-issues-empty" className={`${td} text-slate-500`}>No issues match.</td></tr>}
          </tbody>
        </table>
      </div>
      {d && (
        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span data-testid="tv-issues-total">{d.total} issue(s){d.scan_truncated ? " · showing the newest results only" : ""}</span>
          <div className="flex gap-2"><button data-testid="tv-issues-prev" disabled={f.page <= 1} onClick={() => set("page", f.page - 1)} className="disabled:opacity-40">Prev</button><span>{f.page} / {pages}</span><button data-testid="tv-issues-next" disabled={f.page >= pages} onClick={() => set("page", f.page + 1)} className="disabled:opacity-40">Next</button></div>
        </div>
      )}
    </div>
  );
}
